const API_PATH = '/api/public/zikmunda/leads';
const TIMEOUT_MS = 30000;

const propertyTypes = {
    'Dům': 'house', 'Byt': 'apartment', 'Pozemek': 'land',
    'Komerční objekt': 'commercial', 'Kancelářské prostory': 'office',
    'Chalupa / Chata': 'cottage', 'Developerský projekt': 'development'
};
const propertyConditions = {
    'Velmi dobrý': 'veryGood', 'Dobrý': 'good', 'Špatný': 'poor',
    'Ve výstavbě': 'underConstruction', 'Novostavba': 'newBuild',
    'Před rekonstrukcí': 'beforeRenovation', 'Po rekonstrukci': 'afterRenovation'
};
const ownerships = { 'Osobní': 'personal', 'Družstevní': 'cooperative' };
const purposes = {
    'Koupě': 'purchase', 'Rekonstrukce': 'renovation',
    'Refinancování': 'refinance', 'Vypořádání SJM': 'maritalSettlement',
    'Americká hypotéka': 'americanMortgage', 'Nemovitost na pronájem': 'rentalInvestment'
};
const fundingSources = {
    'Vlastní zdroje': 'ownFunds', 'Zástava nemovitostí': 'propertyCollateral'
};
const featureNames = {
    balkon: 'balcony', bazen: 'pool', developerskeProjekty: 'newDevelopment',
    garaz: 'garage', parkovaciStani: 'parking', sklep: 'cellar', terasa: 'terrace'
};

export class FormsApiError extends Error {
    constructor(message, code = 'request_failed', errors = {}) {
        super(message);
        this.name = 'FormsApiError';
        this.code = code;
        this.errors = errors;
    }
}

function requiredText(value, label, max) {
    const text = String(value == null ? '' : value).trim();
    if (!text || text.length > max) throw new FormsApiError(`Zkontrolujte prosím pole ${label}.`, 'validation_failed');
    return text;
}

function optionalText(value, label, max) {
    const text = String(value == null ? '' : value).trim();
    if (text.length > max) throw new FormsApiError(`Pole ${label} je příliš dlouhé.`, 'validation_failed');
    return text;
}

function numeric(value, label, min, max, integer = false) {
    if (value === '' || value === null || value === undefined ||
        (typeof value !== 'number' && typeof value !== 'string') ||
        (typeof value === 'string' && !/^\d+(\.\d+)?$/.test(value.trim()))) {
        throw new FormsApiError(`Zkontrolujte prosím hodnotu ${label}.`, 'validation_failed');
    }
    const number = Number(value);
    if (!Number.isFinite(number) || number < min || number > max || (integer && !Number.isInteger(number))) {
        throw new FormsApiError(`Zkontrolujte prosím hodnotu ${label}.`, 'validation_failed');
    }
    return number;
}

function selected(value, choices, label) {
    if (!Object.prototype.hasOwnProperty.call(choices, value)) {
        throw new FormsApiError(`Zkontrolujte prosím pole ${label}.`, 'validation_failed');
    }
    return choices[value];
}

function commonFields(formType, form) {
    const email = requiredText(form.email, 'e-mail', 254);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        throw new FormsApiError('Zadejte platný e-mail.', 'validation_failed', { email: ['Zadejte platný e-mail.'] });
    }
    const phone = optionalText(form.tel, 'telefon', 40);
    if ((formType !== 'contact' && !phone) || (phone && (!/^\+?[\d\s().-]{9,40}$/.test(phone) || phone.replace(/\D/g, '').length < 9))) {
        throw new FormsApiError('Zadejte platný telefon.', 'validation_failed', { phone: ['Zadejte platný telefon.'] });
    }
    if (form.souhlas !== true) throw new FormsApiError('Potvrďte prosím seznámení s podmínkami a informacemi o zpracování údajů.', 'validation_failed');
    const captchaToken = requiredText(form.captcharesponse, 'ověření CAPTCHA', 8192);
    return {
        formType,
        firstName: requiredText(form.firstName, 'jméno', 80),
        lastName: requiredText(form.lastName, 'příjmení', 100),
        email,
        phone,
        acceptPrivacy: true,
        captchaToken,
        website: '',
        sourcePath: typeof window === 'undefined' ? '/' : window.location.pathname.slice(0, 250)
    };
}

function locationFields(form, addressRequired) {
    const postalCode = requiredText(form.zip, 'PSČ', 6);
    if (!/^\d{3} ?\d{2}$/.test(postalCode)) {
        throw new FormsApiError('Zadejte platné PSČ.', 'validation_failed', { postalCode: ['Zadejte platné PSČ.'] });
    }
    const fields = {
        city: optionalText(form.city, 'město', 120),
        postalCode
    };
    if (addressRequired) fields.address = requiredText(form.adress, 'adresa', 250);
    return fields;
}

// Build only the documented public fields; recipient and template routing belongs to BsK.
export function buildLead(formType, form, state) {
    const common = commonFields(formType, form);
    if (formType === 'contact') {
        const subject = requiredText(form.subject, 'předmět', 100);
        if (subject.length < 3) throw new FormsApiError('Předmět musí mít alespoň 3 znaky.', 'validation_failed');
        return { ...common, contact: { subject, message: optionalText(form.message, 'zpráva', 2000) } };
    }
    if (formType === 'valuation') {
        const info = state.otherRealEstateInfo || {};
        const layout = state.dispozice === 'Atypické' ? 'atypical' : String(state.dispozice || '');
        if (!/^[1-5]$/.test(layout) && layout !== 'atypical') throw new FormsApiError('Zkontrolujte prosím dispozici.', 'validation_failed');
        const estimate = state.realEstateEstimate === '' || state.realEstateEstimate == null
            ? null : numeric(state.realEstateEstimate, 'odhadu ceny', 0, 1000000000);
        return {
            ...common,
            valuation: {
                propertyType: selected(state.druh_nemovitosti, propertyTypes, 'druh nemovitosti'),
                propertyCondition: selected(state.stav_nemovitosti, propertyConditions, 'stav nemovitosti'),
                ownership: selected(state.typ_vlastnictvi, ownerships, 'vlastnictví'),
                layout,
                areaM2: numeric(state.velikost, 'plocha', 0.000001, 1000000),
                ownerEstimateCzk: estimate,
                ...locationFields(form, true),
                notes: optionalText(info.customerMessage, 'poznámka', 2000),
                features: Object.keys(featureNames).filter(key => info[key] === true).map(key => featureNames[key])
            }
        };
    }
    if (formType === 'mortgage') {
        const amountCzk = numeric(state.morgageAmountValue, 'výše hypotéky', 0.000001, 1000000000);
        const ownFundsCzk = numeric(state.morgageResourcesValue, 'vlastní zdroje', 0, amountCzk);
        const payment = state.morgageEstimateValue === '' || state.morgageEstimateValue == null
            ? null : numeric(state.morgageEstimateValue, 'měsíční splátka', 0, 1000000000);
        return {
            ...common,
            mortgage: {
                purpose: selected(form.ucel, purposes, 'účel hypotéky'),
                fundingSource: selected(form.zdroje, fundingSources, 'původ zdrojů'),
                fixedRateYears: numeric(form.fixace, 'délka fixace', 1, 11, true),
                amountCzk,
                ownFundsCzk,
                termYears: numeric(state.morgageLength, 'splatnost', 1, 50, true),
                estimatedMonthlyPaymentCzk: payment,
                ...locationFields(form, false)
            }
        };
    }
    throw new FormsApiError('Neznámý typ formuláře.', 'validation_failed');
}

const pendingRequests = {};

function createRequestId() {
    if (typeof window === 'undefined' || !window.crypto || !window.crypto.getRandomValues) {
        throw new FormsApiError('Bezpečné odeslání není v tomto prohlížeči dostupné. Kontaktujte mě prosím přímo.', 'crypto_unavailable');
    }
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function getRequestId(formType, lead) {
    const { captchaToken, ...stableFields } = lead;
    const fingerprint = JSON.stringify(stableFields);
    if (!pendingRequests[formType] || pendingRequests[formType].fingerprint !== fingerprint) {
        pendingRequests[formType] = { fingerprint, requestId: createRequestId() };
    }
    return pendingRequests[formType].requestId;
}

const defaultMessages = {
    validation_failed: 'Zkontrolujte prosím vyplněné údaje.',
    captcha_failed: 'Ověření CAPTCHA se nezdařilo. Zkuste ho prosím znovu.',
    honeypot_rejected: 'Požadavek se nepodařilo zpracovat.',
    submission_in_progress: 'Požadavek se ještě zpracovává. Zkuste to prosím za chvíli.',
    idempotency_conflict: 'Údaje se mezitím změnily. Zkuste požadavek odeslat znovu.',
    payload_too_large: 'Zpráva je příliš dlouhá.',
    unsupported_media_type: 'Požadavek má nepodporovaný formát. Kontaktujte mě prosím přímo.',
    rate_limited: 'Odeslali jste příliš mnoho požadavků. Zkuste to prosím později.',
    send_budget_exceeded: 'Odesílání je dočasně nedostupné. Kontaktujte mě prosím přímo.',
    forms_disabled: 'Formulář je dočasně nedostupný. Kontaktujte mě prosím přímo.',
    captcha_unavailable: 'Ověření CAPTCHA je dočasně nedostupné. Kontaktujte mě prosím přímo.',
    protection_unavailable: 'Ochrana formuláře je dočasně nedostupná. Kontaktujte mě prosím přímo.',
    storage_unavailable: 'Odesílání je dočasně nedostupné. Kontaktujte mě prosím přímo.',
    delivery_failed: 'Doručení se nepodařilo potvrdit. Kontaktujte mě prosím přímo.',
    delivery_unknown: 'Doručení zatím nemůžeme potvrdit. Kontaktujte mě prosím přímo.'
};

function parseResponse(text) {
    try { return JSON.parse(text); } catch (_) { return null; }
}

function retryDelaySeconds(bodySeconds, header) {
    const body = Number(bodySeconds);
    if (bodySeconds != null && Number.isFinite(body) && body > 0) return Math.ceil(body);
    const seconds = Number(header);
    if (header && Number.isFinite(seconds) && seconds > 0) return Math.ceil(seconds);
    const date = header && Date.parse(header);
    return Number.isFinite(date) ? Math.max(0, Math.ceil((date - Date.now()) / 1000)) : 0;
}

// Timeouts and transport failures are uncertain: keep the request ID for an explicit user retry.
export async function submitLead(formType, form, state) {
    const baseUrl = process.env.REACT_APP_BSK_FORMS_API_BASE_URL;
    if (!baseUrl) throw new FormsApiError('Kontaktní formulář není nakonfigurovaný. Kontaktujte mě prosím přímo.', 'configuration_missing');
    let endpoint;
    try { endpoint = new URL(API_PATH, baseUrl); } catch (_) {
        throw new FormsApiError('Adresa kontaktního formuláře není platná. Kontaktujte mě prosím přímo.', 'configuration_invalid');
    }
    if (endpoint.protocol !== 'https:') throw new FormsApiError('Kontaktní formulář vyžaduje zabezpečené spojení.', 'configuration_invalid');
    const lead = buildLead(formType, form, state);
    const payload = { requestId: getRequestId(formType, lead), ...lead };
    const body = JSON.stringify(payload);
    if (new Blob([body]).size > 16384) throw new FormsApiError('Zpráva je příliš dlouhá.', 'payload_too_large');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    let response;
    try {
        response = await fetch(endpoint.toString(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'omit',
            body,
            signal: controller.signal
        });
        const data = parseResponse(await response.text());
        if ((response.status === 200 || response.status === 201) && data && data.success === true &&
            data.status === 'sent' && typeof data.submissionId === 'string' && data.submissionId) {
            delete pendingRequests[formType];
            return data;
        }
        if (data && data.success === false) {
            const fallback = defaultMessages[data.code] || 'Odeslání se nezdařilo. Kontaktujte mě prosím přímo.';
            const seconds = retryDelaySeconds(data.retryAfterSeconds, response.headers.get('Retry-After'));
            const wait = (response.status === 429 || data.code === 'submission_in_progress') && seconds > 0
                ? ` Zkuste to znovu za ${seconds} sekund.` : '';
            if (data.code === 'idempotency_conflict') delete pendingRequests[formType];
            throw new FormsApiError(`${typeof data.message === 'string' && data.message ? data.message : fallback}${wait}`, data.code, data.errors || {});
        }
        throw new FormsApiError('Odpověď serveru není platná. Doručení se nepodařilo potvrdit; kontaktujte mě prosím přímo.', 'invalid_response');
    } catch (error) {
        if (error instanceof FormsApiError) throw error;
        if (error.name === 'AbortError') throw new FormsApiError('Odeslání trvá déle než obvykle. Doručení se nepodařilo potvrdit; ověřte ho prosím před dalším pokusem.', 'timeout');
        throw new FormsApiError('Spojení se serverem se nezdařilo. Doručení se nepodařilo potvrdit; kontaktujte mě prosím přímo.', 'network_error');
    } finally {
        clearTimeout(timer);
    }
}
